/**
 * Octokit is loaded on demand, not at module scope.
 *
 * It is one of the heaviest dependencies in the app, and it is needed only by
 * people who sign in with GitHub — yet a static import put it in the initial
 * download for everyone, including users who never sign in at all. Every
 * method already awaits, so deferring costs nothing at the call site.
 *
 * The promise is cached, so concurrent calls share one download.
 */
let octokitModule = null;
function loadOctokit() {
    if (!octokitModule)
        octokitModule = import('octokit');
    return octokitModule;
}
import { exportToMultiFile, multiFileToExport, multiFileToContents, contentUnchanged, parseMultiFile, MULTIFILE_NAMES, SYNC_METADATA_FILE, } from '../providers/multifile';
const DEFAULT_BRANCH = 'main';
const DEFAULT_DATA_PATH = ''; // Store at repo root
// Browser-compatible base64 encoding (handles UTF-8)
function encodeBase64(str) {
    return btoa(unescape(encodeURIComponent(str)));
}
// Browser-compatible base64 decoding (handles UTF-8)
function decodeBase64(base64) {
    return decodeURIComponent(escape(atob(base64)));
}
// Helper to build file path (handles empty dataPath)
function buildPath(dataPath, filePath) {
    return dataPath ? `${dataPath}/${filePath}` : filePath;
}
/**
 * GitHub API service for data synchronization
 */
export class GitHubAPI {
    providerType = 'github';
    client = null;
    config;
    constructor(config) {
        this.config = {
            ...config,
            branch: config.branch ?? DEFAULT_BRANCH,
            dataPath: config.dataPath ?? DEFAULT_DATA_PATH,
        };
    }
    /**
     * The Octokit client, constructed on first use.
     *
     * Two settings that writing depends on:
     *
     * - **No HTTP cache.** GitHub serves file reads with `Cache-Control:
     *   private, max-age=60`, so a browser answered a re-read from its cache
     *   for a minute after a write — with the OLD sha. The next write of that
     *   file then sent the stale sha and was refused ("finance-settings.json
     *   does not match <sha>", 409). Any file written twice within a minute
     *   failed: a batch of uploads, an upload right after creating its account,
     *   finance settings saved in quick succession.
     * - **No automatic retry of a 409.** The retry plugin re-sent the refused
     *   request unchanged — same stale sha — three more times with backoff, and
     *   rapid commits meanwhile hit GitHub's secondary rate limit, whose waits
     *   run to minutes. A 19-file import took twenty minutes and still failed.
     *   `writeRawFile` re-reads and retries a conflict itself.
     */
    async api() {
        if (!this.client) {
            const { Octokit: Ctor } = await loadOctokit();
            this.client = new Ctor({
                auth: this.config.token,
                request: {
                    fetch: (input, init) => fetch(input, { ...init, cache: 'no-store' }),
                },
                retry: { doNotRetry: [400, 401, 403, 404, 409, 422, 451] },
            });
        }
        return this.client;
    }
    /**
     * Bootstrap: ensure the data repository exists (CloudStorageProvider).
     */
    async initialize() {
        const repoExists = await this.checkRepository();
        if (!repoExists) {
            await this.createRepository(true);
        }
    }
    /**
     * Get the authenticated user's information
     */
    async getUser() {
        const { data } = await (await this.api()).rest.users.getAuthenticated();
        return {
            login: data.login,
            name: data.name,
            avatar_url: data.avatar_url,
        };
    }
    /**
     * Check if repository exists and is accessible
     */
    async checkRepository() {
        try {
            await (await this.api()).rest.repos.get({
                owner: this.config.owner,
                repo: this.config.repo,
            });
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * Create the repository if it doesn't exist
     */
    async createRepository(isPrivate = true) {
        const user = await this.getUser();
        if (this.config.owner === user.login) {
            // Create user repository
            await (await this.api()).rest.repos.createForAuthenticatedUser({
                name: this.config.repo,
                private: isPrivate,
                description: 'TrackMind data storage',
                auto_init: true,
            });
        }
        else {
            // Create org repository (requires org permissions)
            await (await this.api()).rest.repos.createInOrg({
                org: this.config.owner,
                name: this.config.repo,
                private: isPrivate,
                description: 'TrackMind data storage',
                auto_init: true,
            });
        }
    }
    /**
     * Get file content from repository
     */
    async getFile(path) {
        try {
            const { data } = await (await this.api()).rest.repos.getContent({
                owner: this.config.owner,
                repo: this.config.repo,
                path: buildPath(this.config.dataPath, path),
                ref: this.config.branch,
            });
            if (Array.isArray(data) || data.type !== 'file') {
                return null;
            }
            // Over 1 MB, the contents API returns the file's sha but no content
            // (encoding "none"). Read as-is, that is an EMPTY file — and a finance
            // year file read as empty is rewritten with only the new rows, deleting
            // the rest. The blob API serves up to 100 MB.
            if (data.encoding === 'none' || (!data.content && (data.size ?? 0) > 0)) {
                const { data: blob } = await (await this.api()).rest.git.getBlob({
                    owner: this.config.owner,
                    repo: this.config.repo,
                    file_sha: data.sha,
                });
                return { content: blob.content, sha: data.sha, encoding: blob.encoding };
            }
            return {
                content: data.content,
                sha: data.sha,
                encoding: data.encoding,
            };
        }
        catch (error) {
            if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
                return null;
            }
            throw error;
        }
    }
    /**
     * Create or update a file in the repository
     */
    async putFile(path, content, message, sha) {
        const { data } = await (await this.api()).rest.repos.createOrUpdateFileContents({
            owner: this.config.owner,
            repo: this.config.repo,
            path: buildPath(this.config.dataPath, path),
            message,
            content: encodeBase64(content),
            sha,
            branch: this.config.branch,
        });
        return {
            sha: data.content?.sha ?? '',
            commitSha: data.commit.sha ?? '',
        };
    }
    /**
     * Delete a file from the repository
     */
    async deleteFile(path, sha, message) {
        await (await this.api()).rest.repos.deleteFile({
            owner: this.config.owner,
            repo: this.config.repo,
            path: buildPath(this.config.dataPath, path),
            message,
            sha,
            branch: this.config.branch,
        });
    }
    /**
     * Get all files in the data directory
     */
    async listFiles() {
        try {
            const { data } = await (await this.api()).rest.repos.getContent({
                owner: this.config.owner,
                repo: this.config.repo,
                path: this.config.dataPath || '.',
                ref: this.config.branch,
            });
            if (!Array.isArray(data)) {
                return [];
            }
            return data
                .filter((item) => item.type === 'file')
                .map((item) => item.name);
        }
        catch (error) {
            if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
                return [];
            }
            throw error;
        }
    }
    /**
     * Get recent commits for the data directory
     */
    async getRecentCommits(limit = 10) {
        const { data } = await (await this.api()).rest.repos.listCommits({
            owner: this.config.owner,
            repo: this.config.repo,
            sha: this.config.branch,
            path: this.config.dataPath || undefined, // undefined = all commits
            per_page: limit,
        });
        return data.map((commit) => ({
            sha: commit.sha,
            message: commit.commit.message,
            author: commit.commit.author?.name ?? 'Unknown',
            date: commit.commit.author?.date ?? '',
        }));
    }
    /**
     * Legacy single-file read (data.json) — pull fallback for repos written
     * before the multi-file format. Never written anymore.
     */
    async loadLegacySingleFile() {
        const file = await this.getFile('data.json');
        if (!file) {
            return null;
        }
        const content = decodeBase64(file.content);
        return JSON.parse(content);
    }
    /**
     * Get sync metadata (CloudStorageProvider). Maps the legacy GitHub-only
     * `lastSyncSha` key onto the canonical `lastSyncRef` when reading repos
     * written by older clients.
     */
    async getSyncMetadata() {
        const file = await this.getFile(SYNC_METADATA_FILE);
        if (!file) {
            return null;
        }
        const content = decodeBase64(file.content);
        const parsed = JSON.parse(content);
        if (!parsed.lastSyncRef && parsed.lastSyncSha) {
            parsed.lastSyncRef = parsed.lastSyncSha;
        }
        return parsed;
    }
    /**
     * Save sync metadata (CloudStorageProvider). Writes the legacy
     * `lastSyncSha` alongside `lastSyncRef` so older clients still work.
     */
    async saveSyncMetadata(metadata) {
        const existing = await this.getFile(SYNC_METADATA_FILE);
        await this.putFile(SYNC_METADATA_FILE, JSON.stringify({ ...metadata, lastSyncSha: metadata.lastSyncRef ?? '' }, null, 2), 'Update sync metadata', existing?.sha);
    }
    /**
     * Check for remote changes since last sync (CloudStorageProvider).
     */
    async hasRemoteChanges(lastSyncRef) {
        const commits = await this.getRecentCommits(1);
        if (commits.length === 0) {
            return false;
        }
        return commits[0].sha !== lastSyncRef;
    }
    /**
     * The remote's current sync ref = latest commit sha (CloudStorageProvider).
     */
    async getRemoteRef() {
        const commits = await this.getRecentCommits(1);
        return commits.length > 0 ? commits[0].sha : null;
    }
    /**
     * Raw file access (CloudStorageProvider) — used by finance. Decodes with
     * the UTF-8-safe helper; the old SyncEngine implementation used bare
     * atob() and corrupted non-ASCII content.
     */
    async readRawFile(path) {
        const file = await this.getFile(path);
        if (!file) {
            return null;
        }
        return decodeBase64(file.content);
    }
    /**
     * Create or replace a file. A 409 means the file changed after we read its
     * sha (another device, or a write a moment ago): read it again and retry,
     * once — this writes whole files, so the newer sha is all that's needed.
     */
    async writeRawFile(path, content, message) {
        for (let attempt = 0;; attempt++) {
            const existing = await this.getFile(path);
            try {
                await this.putFile(path, content, message, existing?.sha);
                return;
            }
            catch (error) {
                const status = error && typeof error === 'object' && 'status' in error ? error.status : null;
                if ((status === 409 || status === 422) && attempt === 0)
                    continue;
                throw error;
            }
        }
    }
    // ==========================================================
    // Multi-file load/save (CloudStorageProvider) — serialization
    // lives in providers/multifile.ts, shared with Google Drive.
    // ==========================================================
    /**
     * Load the complete export (CloudStorageProvider).
     *
     * Fetches core.json first; if present, fetches the other five group files
     * in parallel. Falls back to the legacy single-file data.json for repos
     * written before the multi-file format.
     */
    /**
     * Read the whole export — all seven files in ONE wave.
     *
     * It used to fetch core.json, wait, and then fetch the other six: two
     * sequential waves to decide something the other six do not depend on
     * (whether this is a legacy single-file repo). That doubled the latency of
     * every read for no information, and read latency is what turns a slow
     * request into a timeout somebody has to retry.
     *
     * A missing core.json still means legacy, and the six speculative fetches
     * are then thrown away — a rare case, on repos not written to since the
     * multi-file layout landed, and cheap because they ran in parallel with the
     * fetch that decided it.
     *
     * Unlike the write path, this is NOT opt-in: it reads exactly the same
     * bytes and produces exactly the same ExportData, so there is nothing for a
     * caller to choose between.
     */
    async loadExportData() {
        const files = await Promise.all(MULTIFILE_NAMES.map((n) => this.getFile(n)));
        const coreIndex = MULTIFILE_NAMES.indexOf('core.json');
        if (!files[coreIndex]) {
            // Legacy single-file repo or brand-new empty repo.
            return this.loadLegacySingleFile();
        }
        const contents = {};
        MULTIFILE_NAMES.forEach((name, i) => {
            contents[name] = files[i] ? decodeBase64(files[i].content) : null;
        });
        return multiFileToExport(parseMultiFile(contents));
    }
    /**
     * Save the complete export in the multi-file layout (CloudStorageProvider).
     *
     * SHA fetches are parallel; the PUTs must be sequential because each one
     * creates a commit and concurrent commits to the same branch conflict.
     *
     * `onlyChanged` skips the files whose substance did not move (see
     * `contentUnchanged`), turning a one-task save from seven sequential
     * commits into one. It is OPT-IN and off by default, so the app syncs
     * exactly as it always has — this is a live app with real users, and a
     * latency win is not worth changing how it writes their data underneath
     * them. The MCP server opts in, because there the seven-commit sequence
     * was long enough to time out the request, land the data anyway, and get
     * retried into a duplicate.
     *
     * core.json is always written even when opted in: it carries `exportedAt`
     * for the whole export (`multiFileToExport` reads the export's timestamp
     * from this file and no other), so skipping it would freeze "last synced".
     * The other six files' timestamps are written but never read back, which is
     * what makes them safe to leave alone.
     *
     * @returns the last commit sha as the sync ref.
     */
    async saveExportData(data, message, options = {}) {
        const multi = exportToMultiFile(data);
        const contents = multiFileToContents(multi);
        const commitMessage = message ?? `Sync: ${new Date().toISOString()}`;
        const existing = await Promise.all(MULTIFILE_NAMES.map((n) => this.getFile(n)));
        let lastCommitSha = '';
        for (let i = 0; i < MULTIFILE_NAMES.length; i++) {
            const name = MULTIFILE_NAMES[i];
            const current = existing[i];
            if (options.onlyChanged
                && name !== 'core.json'
                && current
                && contentUnchanged(decodeBase64(current.content), contents[name])) {
                continue;
            }
            const label = name.replace('.json', '');
            const result = await this.putFile(name, contents[name], `${commitMessage} - ${label}`, current?.sha);
            lastCommitSha = result.commitSha;
        }
        return lastCommitSha;
    }
}
//# sourceMappingURL=api.js.map